import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { usePlan } from '../contexts/PlanContext';
import { 
  ChartBar, 
  DownloadSimple, 
  FileXls, 
  FilePdf,
  Package,
  CurrencyDollar,
  Scales,
  Users,
  Lock,
  Truck,
  CalendarBlank
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { UpgradeBanner } from '../components/UpgradeBanner';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportsPage() {
  const { language , tx } = useLanguage();
  const { hasFeature } = usePlan();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  // Shipping totals (Day / Week / Month / Year / All) — independent of date filters
  const [shipPeriod, setShipPeriod] = useState('month');
  const [shipData, setShipData] = useState(null);
  const [shipLoading, setShipLoading] = useState(true);

  useEffect(() => { fetchReport(); }, []);
  useEffect(() => { fetchShipping(shipPeriod); }, [shipPeriod]);

  const fetchShipping = async (period) => {
    setShipLoading(true);
    try {
      const res = await api.get('/reports/shipping-totals', { params: { period } });
      setShipData(res.data);
    } catch (err) {
      toast.error(tx('Failed to load shipping totals', 'Error al cargar totales de envíos', 'Failed to load shipping totals'));
    } finally {
      setShipLoading(false);
    }
  };

  const fetchReport = async (start, end) => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      const res = await api.get('/reports/revenue', { params });
      setReport(res.data);
    } catch (error) {
      toast.error(tx('Failed to load report', 'Error al cargar reporte', 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => fetchReport(startDate, endDate);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const res = await api.get(`/reports/export/${format}?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Track_My_Package_Report.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(tx(`${format.toUpperCase()} downloaded`, `${format.toUpperCase()} descargado`, `${format.toUpperCase()} downloaded`));
    } catch (error) {
      toast.error(tx('Export failed', 'Error al exportar', 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const statusLabel = (s) => ({
    intake_miami: tx('Intake Miami', 'Recibido Miami', 'Intake Miami'),
    in_transit: tx('In Transit', 'En Transito', 'In Transit'),
    arrived_san_jose: tx('Arrived San Jose', 'Llego San Jose', 'Arrived San Jose'),
    ready_for_pickup: tx('Ready for Pickup', 'Listo para Recoger', 'Ready for Pickup'),
    delivered: tx('Delivered', 'Entregado', 'Livré'),
  }[s] || s);

  const statusColor = (s) => ({
    intake_miami: 'bg-blue-500',
    in_transit: 'bg-amber-500',
    arrived_san_jose: 'bg-teal-500',
    ready_for_pickup: 'bg-green-500',
    delivered: 'bg-emerald-600',
  }[s] || 'bg-gray-400');

  return (
    <Layout title={tx('Reports', 'Reportes', 'Rapports')}>
      <div className="space-y-6" data-testid="reports-page">
        {/* ═══════════ SHIPPING TOTALS (Day / Week / Month / Year / All) ═══════════ */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="kpi-card overflow-hidden" data-testid="shipping-totals-section">
          <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h3 className="text-base font-bold uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Bebas Neue' }}>
                <Truck size={20} className="text-[#0077FF]" weight="duotone" />
                {tx('SHIPPING TOTALS', 'TOTALES DE ENVÍOS', 'TOTAUX EXPÉDITIONS')}
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {tx(
                  'Packages received in San José or delivered',
                  'Paquetes recibidos en San José o entregados',
                  'Colis reçus à San José ou livrés'
                )}
              </p>
            </div>
            {/* Period selector */}
            <div className="inline-flex bg-gray-100 rounded-lg p-1 text-xs font-bold" role="tablist" data-testid="shipping-period-toggle">
              {[
                { id: 'day', label: tx('Today', 'Hoy', 'Aujourd\'hui') },
                { id: 'week', label: tx('Week', 'Semana', 'Semaine') },
                { id: 'month', label: tx('Month', 'Mes', 'Mois') },
                { id: 'year', label: tx('Year', 'Año', 'Année') },
                { id: 'all', label: tx('All Time', 'Total', 'Total') },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => setShipPeriod(p.id)}
                  className={`px-3 py-1.5 rounded-md transition-colors ${shipPeriod === p.id ? 'bg-white text-[#0077FF] shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                  data-testid={`ship-period-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {shipLoading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="w-6 h-6 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : shipData ? (
            <div className="p-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-3" data-testid="ship-stat-packages">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-blue-700 font-bold">
                  <Package size={12} weight="fill" /> {tx('Packages', 'Paquetes', 'Colis')}
                </div>
                <p className="text-2xl font-bold text-blue-700 mt-1">{shipData.totals.packages.toLocaleString()}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {tx('Avg', 'Prom', 'Moy')}: {shipData.totals.avg_kg_per_pkg} kg
                </p>
              </div>
              <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3" data-testid="ship-stat-weight">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-700 font-bold">
                  <Scales size={12} weight="fill" /> {tx('Total Weight', 'Peso Total', 'Poids Total')}
                </div>
                <p className="text-2xl font-bold text-amber-700 mt-1">{shipData.totals.weight_kg.toLocaleString()} kg</p>
                <p className="text-[10px] text-gray-500 mt-0.5">≈ {shipData.totals.weight_lbs.toLocaleString()} lbs</p>
              </div>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3" data-testid="ship-stat-revenue">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-700 font-bold">
                  <CurrencyDollar size={12} weight="fill" /> {tx('Revenue (billed)', 'Ingresos', 'Revenu')}
                </div>
                <p className="text-2xl font-bold text-emerald-700 mt-1">${shipData.totals.revenue_usd.toLocaleString()}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  ${shipData.totals.avg_revenue_per_pkg} / {tx('pkg', 'paq', 'colis')}
                </p>
              </div>
              <div className="rounded-xl border border-violet-100 bg-violet-50/50 p-3" data-testid="ship-stat-period">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-violet-700 font-bold">
                  <CalendarBlank size={12} weight="fill" /> {tx('Period', 'Período', 'Période')}
                </div>
                <p className="text-2xl font-bold text-violet-700 mt-1 capitalize">
                  {shipPeriod === 'day' ? tx('Today', 'Hoy', 'Today') :
                    shipPeriod === 'week' ? tx('This Week', 'Esta Semana', 'Semaine') :
                    shipPeriod === 'month' ? tx('This Month', 'Este Mes', 'Mois') :
                    shipPeriod === 'year' ? tx('This Year', 'Este Año', 'Année') :
                    tx('All Time', 'Total', 'Total')}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {tx('Updated', 'Actualizado', 'À jour')}: {new Date(shipData.as_of).toLocaleTimeString()}
                </p>
              </div>

              {/* Mini series chart (last 12 buckets) */}
              {shipData.series && shipData.series.length > 0 && (
                <div className="col-span-2 lg:col-span-4 mt-2 rounded-xl border border-gray-100 bg-white p-3" data-testid="shipping-trend-chart">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">
                    {tx('Trend — last 12', 'Tendencia — últimos 12', 'Tendance — derniers 12')} {shipPeriod === 'day' ? tx('days', 'días', 'jours') : shipPeriod === 'week' ? tx('weeks', 'semanas', 'semaines') : shipPeriod === 'year' ? tx('years', 'años', 'années') : tx('months', 'meses', 'mois')}
                  </p>
                  <div className="flex items-end gap-1.5 h-24">
                    {(() => {
                      const maxKg = Math.max(1, ...shipData.series.map(s => s.weight_kg));
                      return shipData.series.map((s, i) => {
                        const heightPct = (s.weight_kg / maxKg) * 100;
                        return (
                          <div key={s.period} className="flex-1 flex flex-col items-center group" title={`${s.period}: ${s.packages} pkg / ${s.weight_kg} kg / $${s.revenue_usd}`}>
                            <div className="w-full flex-1 flex items-end">
                              <div
                                className="w-full rounded-t bg-gradient-to-t from-[#0077FF] to-[#4DA3FF] transition-all group-hover:from-[#FF7A00] group-hover:to-[#FFB347]"
                                style={{ height: `${Math.max(2, heightPct)}%` }}
                              />
                            </div>
                            <p className="text-[8px] text-gray-400 mt-1 truncate w-full text-center">{s.period.slice(-5)}</p>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </motion.div>

        {/* Filters + Export */}
        <div className="flex flex-col sm:flex-row gap-4 items-end justify-between">
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <Label className="form-label text-xs">{tx('From', 'Desde', 'From')}</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="rounded-lg w-40" data-testid="report-start-date" />
            </div>
            <div>
              <Label className="form-label text-xs">{tx('To', 'Hasta', 'To')}</Label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="rounded-lg w-40" data-testid="report-end-date" />
            </div>
            <Button onClick={handleFilter} variant="outline" className="rounded-lg" data-testid="filter-btn">
              {tx('Filter', 'Filtrar', 'Filter')}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => hasFeature('excel_export') ? handleExport('excel') : toast.error('Excel export requires the Professional plan.')}
              disabled={exporting}
              variant="outline"
              className={`rounded-lg ${hasFeature('excel_export') ? 'border-green-400 text-green-700 hover:bg-green-50' : 'border-gray-300 text-gray-400 cursor-not-allowed'}`}
              data-testid="export-excel-btn"
            >
              {hasFeature('excel_export') ? <FileXls size={18} className="mr-2" /> : <Lock size={16} className="mr-2" />}
              {tx('Export Excel', 'Exportar Excel', 'Exporter Excel')}
            </Button>
            <Button
              onClick={() => hasFeature('pdf_export') ? handleExport('pdf') : toast.error('PDF export requires the Professional plan.')}
              disabled={exporting}
              variant="outline"
              className={`rounded-lg ${hasFeature('pdf_export') ? 'border-red-400 text-red-700 hover:bg-red-50' : 'border-gray-300 text-gray-400 cursor-not-allowed'}`}
              data-testid="export-pdf-btn"
            >
              {hasFeature('pdf_export') ? <FilePdf size={18} className="mr-2" /> : <Lock size={16} className="mr-2" />}
              {tx('Export PDF', 'Exportar PDF', 'Exporter PDF')}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : report && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Package, label: tx('Packages', 'Paquetes', 'colis'), value: report.total_packages, color: 'text-blue-500', bg: 'bg-blue-50' },
                { icon: Scales, label: tx('Total Weight', 'Peso Total', 'Total Weight'), value: `${report.total_weight_lbs} lbs`, color: 'text-amber-500', bg: 'bg-amber-50' },
                { icon: CurrencyDollar, label: tx('Revenue', 'Ingresos', 'revenu'), value: `$${report.total_revenue_usd.toFixed(2)}`, color: 'text-[#0077FF]', bg: 'bg-teal-50' },
                { icon: Users, label: tx('Customers', 'Clientes', 'Customers'), value: report.customers_count, color: 'text-purple-500', bg: 'bg-purple-50' },
              ].map((kpi, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="kpi-card"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${kpi.bg}`}>
                      <kpi.icon size={22} className={kpi.color} weight="duotone" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">{kpi.label}</p>
                      <p className="text-xl font-bold">{kpi.value}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Revenue cards row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="kpi-card">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{tx('Total Invoiced', 'Total Facturado', 'Total Invoiced')}</p>
                <p className="text-2xl font-bold text-[#0077FF]">${report.total_invoiced_usd.toFixed(2)}</p>
                <p className="text-xs text-gray-400 mt-1">{report.invoices_count} {tx('invoices', 'facturas', 'invoices')}</p>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="kpi-card">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{tx('Paid', 'Pagado', 'Paid')}</p>
                <p className="text-2xl font-bold text-green-600">${report.total_paid_usd.toFixed(2)}</p>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="kpi-card">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{tx('Pending', 'Pendiente', 'Pending')}</p>
                <p className="text-2xl font-bold text-[#FFB300]">${report.total_pending_usd.toFixed(2)}</p>
              </motion.div>
            </div>

            {/* Status breakdown + Customer summary */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Status */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="kpi-card">
                <h3 className="font-bold text-lg mb-4" style={{ fontFamily: 'Bebas Neue' }}>
                  {tx('BY STATUS', 'POR ESTADO', 'BY STATUS')}
                </h3>
                <div className="space-y-3">
                  {Object.entries(report.status_breakdown).map(([status, count]) => {
                    const pct = report.total_packages > 0 ? (count / report.total_packages * 100) : 0;
                    return (
                      <div key={status}>
                        <div className="flex justify-between text-sm mb-1">
                          <span>{statusLabel(status)}</span>
                          <span className="font-bold">{count} ({pct.toFixed(0)}%)</span>
                        </div>
                        <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${pct}%` }}
                            transition={{ duration: 0.5 }}
                            className={`h-full rounded-full ${statusColor(status)}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Top Customers */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="kpi-card">
                <h3 className="font-bold text-lg mb-4" style={{ fontFamily: 'Bebas Neue' }}>
                  {tx('TOP CUSTOMERS', 'TOP CLIENTES', 'Meilleurs Clients')}
                </h3>
                <div className="space-y-3">
                  {report.customer_summary.slice(0, 5).map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-[#0077FF] flex items-center justify-center text-white text-xs font-bold">
                          {i + 1}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-xs text-gray-400">{c.packages} {tx('pkgs', 'paquetes', 'colis')} | {c.weight.toFixed(1)} lbs</p>
                        </div>
                      </div>
                      <p className="font-bold text-[#0077FF]">${c.revenue.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Recent packages table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="kpi-card overflow-hidden">
              <h3 className="font-bold text-lg mb-4" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('RECENT PACKAGES', 'PAQUETES RECIENTES', 'Colis Récents')}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full data-table text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3">Tracking</th>
                      <th className="text-left py-2 px-3">{tx('Customer', 'Cliente', 'Client')}</th>
                      <th className="text-right py-2 px-3">{tx('Weight', 'Peso', 'Poids')}</th>
                      <th className="text-right py-2 px-3">{tx('Cost', 'Costo', 'Coût')}</th>
                      <th className="text-left py-2 px-3">{tx('Status', 'Estado', 'Statut')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.packages.slice(0, 20).map((p, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3 font-mono text-xs">{p.tracking_number}</td>
                        <td className="py-2 px-3">{p.customer_name}</td>
                        <td className="py-2 px-3 text-right">{p.weight_lbs} lbs</td>
                        <td className="py-2 px-3 text-right font-bold">${p.shipping_cost.toFixed(2)}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${statusColor(p.status)}`} />
                          <span className="text-xs">{statusLabel(p.status)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </>
        )}
      </div>
    </Layout>
  );
}
